'use client';

import React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../primitives/dialog';
import { cn } from '../../utils/cn';

import type { ReactNode } from 'react';

/**
 * UzDialog - opinionated wrapper around the shadcn Dialog.
 *
 * Combines header, content, and footer in one prop-driven API so call sites
 * don't have to import six pieces. The trigger remains pluggable via
 * `trigger` so existing buttons can open the dialog without prop drilling.
 */

export interface UzDialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog. asChild semantics. */
  trigger?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Optional content rendered below the title/description inside the pinned
   * dialog header (e.g. jump-nav chips). Stays visible while the body scrolls.
   */
  headerExtra?: ReactNode;
  children?: ReactNode;
  /** Footer slot (typically action buttons). */
  footer?: ReactNode;
  /** Override max-width. Default sm:max-w-lg. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Prevent close on outside click and Escape. */
  forceOpen?: boolean;
}

const sizeClass: Record<NonNullable<UzDialogProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-2xl',
  // 2026-06-25: comfortable wide popup for forms with 3-column grids
  // (e.g. the lead Edit form embedded in the status-changer modal).
  // Stays well below the full-screen 90vw so it still reads as a
  // popup, not a takeover.
  '2xl': 'sm:max-w-4xl',
  full: 'sm:max-w-[90vw]',
};

export const UzDialog = ({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  headerExtra,
  children,
  footer,
  size = 'lg',
  forceOpen,
}: UzDialogProps): React.JSX.Element => (
  <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={forceOpen ? undefined : onOpenChange}>
    {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
    <DialogContent
      // 2026-06-25: cap height at 90vh so tall children (e.g. an
      // embedded edit form) don't push the popup past the viewport;
      // the body slot scrolls instead. Header + footer stay pinned.
      // 2026-09-07: on phones the primitive is edge-to-edge and square. A
      // popup that touches the screen edges reads as a page, not a popup, so
      // below `sm` it keeps a 1rem gap each side and its rounded corners.
      className={cn(
        sizeClass[size],
        'w-[calc(100%-2rem)] rounded-lg p-4 sm:w-full sm:p-6',
        'max-h-[90vh] grid-rows-[auto_1fr_auto]',
      )}
      onInteractOutside={forceOpen ? (e) => e.preventDefault() : undefined}
      onEscapeKeyDown={forceOpen ? (e) => e.preventDefault() : undefined}
    >
      {(title || description || headerExtra) && (
        <DialogHeader>
          {title && <DialogTitle>{title}</DialogTitle>}
          {description && <DialogDescription>{description}</DialogDescription>}
          {headerExtra}
        </DialogHeader>
      )}
      <div className="min-h-0 overflow-y-auto">{children}</div>
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </DialogContent>
  </Dialog>
);
